import type { ReactElement } from "react";
import { useReducer, useState } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Button, Combobox, DropdownButton, Modal, NumberInput } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { DEFAULT_MIN_PROMPTS_TO_GENERATE } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/consts";
import { TopicOptionLabel } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/prompts-table/PromptsTable.styled";
import { PromptsCounter } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/PromptsCounter";
import { allTrackers } from "app/services/track/track";

const StyledNumberInput = styled(NumberInput)`
    width: 100%;
`;

interface IModalState {
    isOpen: boolean;
    selectedTopic: string;
    promptsNumber: number;
    hasError: boolean;
}

type ModalAction =
    | { type: "OPEN"; topic: string; defaultCount: number }
    | { type: "CLOSE" }
    | { type: "SET_TOPIC"; topic: string }
    | { type: "SET_COUNT"; count: number }
    | { type: "SET_ERROR"; hasError: boolean };

const modalReducer = (state: IModalState, action: ModalAction): IModalState => {
    switch (action.type) {
        case "OPEN":
            return { isOpen: true, selectedTopic: action.topic, promptsNumber: action.defaultCount, hasError: false };
        case "CLOSE":
            return { isOpen: false, selectedTopic: "", promptsNumber: 0, hasError: false };
        case "SET_TOPIC":
            return state.selectedTopic === action.topic ? state : { ...state, selectedTopic: action.topic };
        case "SET_COUNT":
            return state.promptsNumber === action.count ? state : { ...state, promptsNumber: action.count };
        case "SET_ERROR":
            return state.hasError === action.hasError ? state : { ...state, hasError: action.hasError };
    }
};

interface IAAutoGenerationModalHook {
    openModal: (topic: string) => void;
    modal: ReactElement;
}

export const useAutoGenerationModal = (
    topics: string[],
    currentPromptsCount: number,
    maxPrompts: number,
    onGeneration: (topic: string, number: number) => Promise<void>,
): IAAutoGenerationModalHook => {
    const maxAvailablePrompts = maxPrompts - currentPromptsCount;
    const defaultFieldValue = Math.min(DEFAULT_MIN_PROMPTS_TO_GENERATE, maxAvailablePrompts);
    const [state, dispatch] = useReducer(modalReducer, {
        isOpen: false,
        selectedTopic: "",
        promptsNumber: defaultFieldValue,
        hasError: false,
    });

    const { isOpen, selectedTopic, promptsNumber, hasError } = state;
    const [isTopicsDropdownOpen, setTopicsDropdownOpen] = useState<boolean>(false);

    const isSaveAvailable = isOpen && !!selectedTopic && !!promptsNumber && !hasError;

    const openModal = (topic: string): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "generate prompts/open");
        dispatch({ type: "OPEN", topic, defaultCount: defaultFieldValue });
    };

    const onTopicClick = (topic: string): void => {
        setTopicsDropdownOpen(false);
        dispatch({ type: "SET_TOPIC", topic });
    };

    const closeModal = (): void => {
        setTopicsDropdownOpen(false);
        dispatch({ type: "CLOSE" });
    };

    const submit = (): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "generate prompts/submit");
        void onGeneration(selectedTopic, promptsNumber);
        closeModal();
    };

    const getNumeralFieldError = (value: number | null): string | null => {
        if (!value) {
            return null;
        }

        //invalid value
        if (value < 0) {
            return translate("llm.prompts.edit.prompts-table.modal.field.prompts-number.error.invalid");
        }
        //exceeding value
        else if (value > maxAvailablePrompts) {
            return translate("llm.prompts.edit.prompts-table.modal.field.prompts-number.error.exceed");
        }

        return null;
    };

    const validateNumeralField = (value: number | null): string | null => {
        const error = getNumeralFieldError(value);
        dispatch({ type: "SET_ERROR", hasError: !!error });
        return error;
    };

    return {
        openModal,
        modal: (
            <Modal
                data-automation="prompts-generation-modal"
                isOpen={isOpen}
                onRequestClose={closeModal}
                shouldCloseOnEsc
                shouldCloseOnOverlayClick
                customStyles={{
                    content: {
                        width: "400px",
                    },
                }}
            >
                <Modal.Header>
                    <Modal.Title tooltip={translate("llm.prompts.edit.prompts-table.generate.tooltip")}>
                        {translate("llm.prompts.edit.prompts-table.generate.title")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Content>
                    <Flex
                        flexDirection="column"
                        padding="16px 0 24px 0"
                        gap="24px"
                    >
                        <Flex
                            flexDirection="column"
                            gap="16px"
                        >
                            <Flex
                                flexDirection="column"
                                gap="4px"
                            >
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[300]}
                                >
                                    {translate("llm.prompts.edit.prompts-table.modal.field.topic.title")}
                                </Text>
                                <Combobox
                                    isOpen={isTopicsDropdownOpen}
                                    onOpenChange={setTopicsDropdownOpen}
                                    placement="bottom"
                                    matchTriggerWidth
                                    data-automation="prompts-generation-topic-dropdown"
                                >
                                    <Combobox.Trigger>
                                        <Flex width="100%">
                                            <DropdownButton isOpen={isTopicsDropdownOpen}>
                                                <Text
                                                    fontVariant="input2"
                                                    color={colorsPalettes.carbon[500]}
                                                >
                                                    {selectedTopic}
                                                </Text>
                                            </DropdownButton>
                                        </Flex>
                                    </Combobox.Trigger>
                                    <Combobox.Content>
                                        {topics.map((topic) => (
                                            <Combobox.Option
                                                key={topic}
                                                id={topic}
                                                textValue={topic}
                                                onClick={() => onTopicClick(topic)}
                                            >
                                                <TopicOptionLabel>{topic}</TopicOptionLabel>
                                            </Combobox.Option>
                                        ))}
                                    </Combobox.Content>
                                </Combobox>
                            </Flex>
                            <Flex
                                flexDirection="column"
                                gap="4px"
                            >
                                <Flex justifyContent="space-between">
                                    <Text
                                        fontVariant="caption"
                                        color={colorsPalettes.carbon[300]}
                                    >
                                        {translate("llm.prompts.edit.prompts-table.modal.field.prompts-number.title")}
                                    </Text>
                                    <PromptsCounter
                                        totalPrompts={currentPromptsCount}
                                        lite
                                    />
                                </Flex>
                                <StyledNumberInput
                                    data-automation="prompts-generation-count-input"
                                    defaultValue={defaultFieldValue}
                                    placeholder={translate(
                                        "llm.prompts.edit.prompts-table.modal.field.prompts-number.placeholder",
                                        { maxAvailablePrompts },
                                    )}
                                    minValue={0}
                                    maxValue={maxAvailablePrompts}
                                    onChange={(value) => dispatch({ type: "SET_COUNT", count: value ?? 0 })}
                                    validate={validateNumeralField}
                                    autoFocus
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        type="flat"
                        onClick={closeModal}
                        data-automation="prompts-generation-cancel"
                    >
                        {translate("global.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        onClick={submit}
                        isDisabled={!isSaveAvailable}
                        data-automation="prompts-generation-submit"
                    >
                        {translate("common.submit")}
                    </Button>
                </Modal.Footer>
            </Modal>
        ),
    };
};
